import React, { memo } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import Checkbox from '@lib/components/CheckBox/CheckBox';
import RadioButton from '@lib/components/RadioButton/RadioButton';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import { isItemsMatch } from '@lib/components/Table/utils';
import { BaseItem, TableProps } from '../../types';
import RowActionsMenuCell from './RowActionsMenuCell/RowActionsMenuCell';
import RowActionsCell from './RowActionsCell/RowActionsCell';
import TableBodyCell from './TableBodyCell';
import styles from './TableBody.module.scss';

interface Props<T extends BaseItem> {
  hasActionsColumn: boolean;
  hasActionsMenuColumn: boolean;
  hasSelectColumn: boolean;
  isSelectionLimitReached?: boolean;
  /** Shared by every row so the radios form one native group (radioSelect only). */
  radioGroupName?: string;
  rowIndex?: number;
  rowItem: T;
  selectionLimit?: number;
}

function TableBodyRow<T extends BaseItem>(props: Props<T> & TableProps<T>) {
  const {
    hasActionsColumn,
    hasActionsMenuColumn,
    hasSelectColumn,
    isSelectionLimitReached,
    radioGroupName,
    rowIndex,
    rowItem,
    selectionLimit,
    actions,
    actionsMenu,
    columns,
    getIsRowDisabled,
    getIsRowSelected = () => false,
    getRowClassName,
    onRowClick,
    radioSelect,
    tree,
    useBulkSelectResult = {} as UseBulkSelectResult<T>,
  } = props;
  const { t } = useTranslation();

  const {
    isAllTableItemsSelected,
    selectedTableItems,
    handleSelectOnlyOneTableItem,
    handleSelectTableItem,
    handleUnselectTableItem,
    unselectedTableItems,
  } = useBulkSelectResult;
  const isDisabled = getIsRowDisabled && getIsRowDisabled(rowItem);
  const isSelectedByBulkSelect =
    (isAllTableItemsSelected &&
      !unselectedTableItems?.some((selectedItem) =>
        isItemsMatch(selectedItem, rowItem),
      )) ||
    selectedTableItems?.some((selectedItem) =>
      isItemsMatch(selectedItem, rowItem),
    );
  const isDisabledByLimit =
    isSelectionLimitReached && !isSelectedByBulkSelect && !radioSelect;
  const treeState = tree?.getRowState(rowItem);
  const isDisabledByDepth =
    tree?.maxDepth != null && (treeState?.depth ?? 0) >= tree.maxDepth;
  const effectiveDisabled =
    isDisabled || isDisabledByLimit || isDisabledByDepth;
  const isDisabledSolelyByLimit =
    isDisabledByLimit && !isDisabled && !isDisabledByDepth;
  const isDisabledSolelyByDepth =
    isDisabledByDepth && !isDisabled && !isDisabledByLimit;
  // `maxDepthTooltip` is optional, so a depth-disabled row without one must
  // keep the tooltip closed rather than open an empty bubble.
  const isDepthTooltipVisible =
    isDisabledSolelyByDepth && !!tree?.maxDepthTooltip;
  const handleRowCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      if (radioSelect && handleSelectOnlyOneTableItem) {
        handleSelectOnlyOneTableItem(rowItem);
      } else {
        handleSelectTableItem(rowItem);
      }
    } else {
      handleUnselectTableItem(rowItem);
    }
  };

  return (
    <tr
      data-key={rowItem.id}
      data-test-id={`table__body-row-${rowItem.id}`}
      className={classNames(
        {
          // [styles.disabled]: isDisabled,
          [styles.trSelected]:
            isSelectedByBulkSelect || getIsRowSelected(rowItem),
          [styles.trClickable]: onRowClick,
        },
        getRowClassName && getRowClassName(rowItem),
      )}
      onClick={onRowClick ? () => onRowClick(rowItem) : undefined}
    >
      {hasSelectColumn && (
        <td className={styles.selectCell}>
          <div className={styles.tdContent}>
            <Tooltip
              body={
                isDepthTooltipVisible
                  ? tree?.maxDepthTooltip
                  : t('selection-limit-reached', { limit: selectionLimit })
              }
              disabled={!isDisabledSolelyByLimit && !isDepthTooltipVisible}
              placement="bottomLeft"
            >
              {radioSelect ? (
                <RadioButton
                  id={rowItem.id}
                  index={rowIndex}
                  name={radioGroupName}
                  checked={isSelectedByBulkSelect}
                  onChange={handleRowCheckboxChange}
                  disabled={effectiveDisabled}
                  data-test-id={`table__row-radio-${rowItem.id}`}
                />
              ) : (
                <Checkbox
                  id={`table-body-checkbox-${rowItem.id}`}
                  name={`table-body-checkbox-${rowItem.id}`}
                  checked={isSelectedByBulkSelect}
                  onChange={handleRowCheckboxChange}
                  disabled={effectiveDisabled}
                  data-test-id={`table__row-checkbox-${rowItem.id}`}
                />
              )}
            </Tooltip>
          </div>
        </td>
      )}
      {columns.map((column, index) => (
        <TableBodyCell<T>
          key={column?.id || uuidv4()}
          column={column}
          isDisabled={isDisabled}
          isFirstColumn={index === 0}
          onToggleTreeRow={tree ? () => tree.onToggleRow(rowItem) : undefined}
          rowItem={rowItem}
          treeState={treeState}
        />
      ))}
      {hasActionsColumn && <RowActionsCell actions={actions} item={rowItem} />}
      {hasActionsMenuColumn && (
        <RowActionsMenuCell actionsMenu={actionsMenu} item={rowItem} />
      )}
    </tr>
  );
}

export default memo(TableBodyRow) as <T extends BaseItem>(
  props: Props<T> & TableProps<T>,
) => React.ReactElement;
